import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";
import axios from "axios";
import Loader from "../../components/Loader";
import DateRangeSelector from "../../components/DateRangeSelector";
import AppConstants from "../../AppConstants";
import { toast } from "react-toastify";
import ImageTotalUsers from '../../images/icon-all-users.png';
import ImageActiveUsers from '../../images/icon-active-users.png';
import ImageInactiveUsers from '../../images/icon-inactive-users.png';
import ImageBlockedUsers from '../../images/icon-blocked-users.png';
import ImageDeletedUsers from '../../images/icon-deleted-users.png';
import ImageTotalRevenue from '../../images/icon-total-revenue.png';
import './index.scss';

interface StatsType {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  blockedUsers: number;
  deletedUsers: number;
  totalRevenue: number;
}

interface UserGrowthDataType {
  month: string;
  users: number;
  date: string;
}

interface RevenueDataType {
  month: string;
  revenue: number;
  date: string;
}

interface LicenseDataType {
  licenseRange: string;
  count: number;
}

interface InspectionDataType {
  type: string;
  count: number;
  percentage: number;
  color: string;
}

interface SubscriptionDataType {
  name: string;
  value: number;
  color: string;
}

interface DashboardApiResponse {
  success: boolean;
  message: string;
  data: {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    blockedUsers: number;
    deletedUsers: number;
    totalRevenue: number;
    userGrowthTrend: UserGrowthDataType[];
    revenueBreakdown: RevenueDataType[];
    licenseDistribution: LicenseDataType[];
    inspectionTypes: {
      type: string;
      count: number;
      percentage: number;
    }[];
    subscriptionInsights: {
      newSubscriptions: number;
      renewalSubscriptions: number;
      newPercentage: number;
      renewalPercentage: number;
    };
    dateRange: {
      startDate: string;
      endDate: string;
    };
  };
}

type DateRangeOption = "Last 30 days" | "Last 90 days" | "Year to date" | "Custom";

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedDateOption, setSelectedDateOption] = useState<DateRangeOption>("Last 90 days");
  const [stats, setStats] = useState<StatsType>({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    blockedUsers: 0,
    deletedUsers: 0,
    totalRevenue: 0
  });
  const [userGrowthData, setUserGrowthData] = useState<UserGrowthDataType[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueDataType[]>([]);
  const [licenseData, setLicenseData] = useState<LicenseDataType[]>([]);
  const [inspectionData, setInspectionData] = useState<InspectionDataType[]>([]);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionDataType[]>([]);

  // Color palette for charts
  const inspectionColors = ["#0088FE", "#00C49F", "#FF8042", "#FFBB28", "#8884D8", "#82CA9D"];
  const subscriptionColors = ["#0088FE", "#FF8042"];

  const formatDateForAPI = (dateString: string): string => {
    // Convert MM/DD/YYYY to YYYY-MM-DD format for API
    const [month, day, year] = dateString.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };


  const fetchDashboardData = async (dateRange?: string, isCustom?: boolean) => {
    setLoading(true);
    try {
      let startDate = '';
      let endDate = '';

      if (dateRange) {
        const [start, end] = dateRange.split(' - ');
        startDate = formatDateForAPI(start);
        endDate = formatDateForAPI(end);
      } else {
        // Default to last 90 days
        const today = new Date();
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(today.getDate() - 90);
        
        endDate = today.toISOString().split('T')[0];
        startDate = ninetyDaysAgo.toISOString().split('T')[0];
      }

      const response = await axios.get<DashboardApiResponse>(
        `${AppConstants.ADMIN_DASHBOARD}/dashboard/metrics`,
        {
          params: {
            startDate,
            endDate
          },
          headers: {
            'Content-Type': 'application/json',
            // Add authorization header if needed
            // 'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        const { data } = response.data;

        // Set stats
        setStats({
          totalUsers: data.totalUsers,
          activeUsers: data.activeUsers,
          inactiveUsers: data.inactiveUsers,
          blockedUsers: data.blockedUsers,
          deletedUsers: data.deletedUsers,
          totalRevenue: data.totalRevenue
        });

        // Set user growth data - use different logic for custom vs preset dates
        const apiUserGrowthData = data.userGrowthTrend || [];
        
        if (isCustom) {
          // For custom dates, always use API data directly (even if empty) to show actual selected date range
          setUserGrowthData(apiUserGrowthData);
        } else {
          // For preset options, ensure all 12 months are displayed with template
          const currentYear = new Date().getFullYear();
          const userGrowthTemplate = [
            { month: "Jan", users: 0, date: `${currentYear}-01-01` },
            { month: "Feb", users: 0, date: `${currentYear}-02-01` },
            { month: "Mar", users: 0, date: `${currentYear}-03-01` },
            { month: "Apr", users: 0, date: `${currentYear}-04-01` },
            { month: "May", users: 0, date: `${currentYear}-05-01` },
            { month: "Jun", users: 0, date: `${currentYear}-06-01` },
            { month: "Jul", users: 0, date: `${currentYear}-07-01` },
            { month: "Aug", users: 0, date: `${currentYear}-08-01` },
            { month: "Sep", users: 0, date: `${currentYear}-09-01` },
            { month: "Oct", users: 0, date: `${currentYear}-10-01` },
            { month: "Nov", users: 0, date: `${currentYear}-11-01` },
            { month: "Dec", users: 0, date: `${currentYear}-12-01` }
          ];
          
          // Merge API data with template to ensure all months are shown
          const mergedUserGrowthData = userGrowthTemplate.map(templateMonth => {
            const apiMonth = apiUserGrowthData.find(apiItem => 
              apiItem.month === templateMonth.month || 
              new Date(apiItem.date).getMonth() === new Date(templateMonth.date).getMonth()
            );
            return apiMonth ? apiMonth : templateMonth;
          });
          
          setUserGrowthData(mergedUserGrowthData);
        }

        // Set revenue data - use different logic for custom vs preset dates
        const apiRevenueData = data.revenueBreakdown || [];
        
        if (isCustom) {
          // For custom dates, always use API data directly (even if empty) to show actual selected date range
          setRevenueData(apiRevenueData);
        } else {
          // For preset options, ensure all 12 months are displayed with template
          const currentYear = new Date().getFullYear();
          const monthsTemplate = [
            { month: "Jan", revenue: 0, date: `${currentYear}-01-01` },
            { month: "Feb", revenue: 0, date: `${currentYear}-02-01` },
            { month: "Mar", revenue: 0, date: `${currentYear}-03-01` },
            { month: "Apr", revenue: 0, date: `${currentYear}-04-01` },
            { month: "May", revenue: 0, date: `${currentYear}-05-01` },
            { month: "Jun", revenue: 0, date: `${currentYear}-06-01` },
            { month: "Jul", revenue: 0, date: `${currentYear}-07-01` },
            { month: "Aug", revenue: 0, date: `${currentYear}-08-01` },
            { month: "Sep", revenue: 0, date: `${currentYear}-09-01` },
            { month: "Oct", revenue: 0, date: `${currentYear}-10-01` },
            { month: "Nov", revenue: 0, date: `${currentYear}-11-01` },
            { month: "Dec", revenue: 0, date: `${currentYear}-12-01` }
          ];
          
          // Merge API data with template to ensure all months are shown
          const mergedRevenueData = monthsTemplate.map(templateMonth => {
            const apiMonth = apiRevenueData.find(apiItem => 
              apiItem.month === templateMonth.month || 
              new Date(apiItem.date).getMonth() === new Date(templateMonth.date).getMonth()
            );
            return apiMonth ? apiMonth : templateMonth;
          });
          
          setRevenueData(mergedRevenueData);
        }

        // Set license distribution data - use different logic for custom vs preset dates
        const apiLicenseData = data.licenseDistribution || [];
        
        if (isCustom) {
          // For custom dates, always use API data directly (even if empty) to show actual selected date range
          setLicenseData(apiLicenseData);
        } else {
          // For preset options, ensure 4 plan sections are displayed with template
          const licenseTemplate = [
            { licenseRange: "16-20", count: 0 },
            { licenseRange: "11-15", count: 0 },
            { licenseRange: "6-10", count: 0 },
            { licenseRange: "1-5", count: 0 }
          ];
          
          // Merge API data with template to ensure 4 plans are shown
          const mergedLicenseData = licenseTemplate.map(templatePlan => {
            const apiPlan = apiLicenseData.find(apiItem => 
              apiItem.licenseRange === templatePlan.licenseRange
            );
            return apiPlan ? apiPlan : templatePlan;
          });
          
          setLicenseData(mergedLicenseData);
        }

        // Set inspection data with colors
        const inspectionWithColors = data.inspectionTypes?.map((item, index) => ({
          ...item,
          color: inspectionColors[index % inspectionColors.length]
        })) || [];
        setInspectionData(inspectionWithColors);

        // Set subscription data
        const subscriptionInsights = data.subscriptionInsights;
        setSubscriptionData([
          {
            name: "New",
            value: subscriptionInsights.newPercentage || 0,
            color: subscriptionColors[0]
          },
          {
            name: "Renewal",
            value: subscriptionInsights.renewalPercentage || 0,
            color: subscriptionColors[1]
          }
        ]);

      } else {
        toast.error('Failed to fetch dashboard data');
      }
    } catch (error: any) {
      console.error('Dashboard API Error:', error);
      toast.error(
        error?.response?.data?.message || 
        error?.message || 
        'Failed to load dashboard data'
      );
    } finally {
      setLoading(false);
    }
  };

  const determineDateOption = (dateRange: string): DateRangeOption => {
    const today = new Date();
    const [startDateStr] = dateRange.split(' - ');
    
    // Parse MM/DD/YYYY format
    const [month, day, year] = startDateStr.split('/');
    const startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    const daysDiff = Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Check if it's Year to date
    const yearStart = new Date(today.getFullYear(), 0, 1);
    if (startDate.getFullYear() === today.getFullYear() && 
        startDate.getMonth() === 0 && 
        startDate.getDate() === 1) {
      return "Year to date";
    }
    
    // Check approximate day ranges (allowing for slight variations)
    if (daysDiff >= 28 && daysDiff <= 32) {
      return "Last 30 days";
    } else if (daysDiff >= 88 && daysDiff <= 92) {
      return "Last 90 days";
    }
    
    return "Custom";
  };

  const handleDateRangeChange = (newDateRange: string, isCustom?: boolean): void => {
    if (isCustom) {
      setSelectedDateOption("Custom");
    } else {
      const dateOption = determineDateOption(newDateRange);
      setSelectedDateOption(dateOption);
    }
    fetchDashboardData(newDateRange, isCustom);
  };

  useEffect(() => {
    // Fetch data with the default 90-day range to match DateRangeSelector
    const today = new Date();
    const ninetyDaysAgo = new Date(today);
    ninetyDaysAgo.setDate(today.getDate() - 90);
    
    const defaultDateRange = `${(ninetyDaysAgo.getMonth() + 1).toString().padStart(2, '0')}/${ninetyDaysAgo.getDate().toString().padStart(2, '0')}/${ninetyDaysAgo.getFullYear()} - ${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getDate().toString().padStart(2, '0')}/${today.getFullYear()}`;
    
    fetchDashboardData(defaultDateRange);
  }, []);

  if (loading) {
    return <Loader text="Loading dashboard..." />;
  }

  return (
    <Container fluid className="py-4 home-dashboard">
      <Row className="mb-4">
        <Col xl={2} lg={4} md={6} sm={12} className="mb-3 mb-md-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="light2 mb-3 gilroy-semibold font-14">Total Users</p>
                  <h4 className="mb-0">{stats.totalUsers.toLocaleString()}</h4>
                </div>
                <img src={ImageTotalUsers} alt='total-users' className="my-auto" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={2} lg={4} md={6} sm={12} className="mb-3 mb-md-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="light2 mb-3 gilroy-semibold font-14">Active Users</p>
                  <h4 className="mb-0">{stats.activeUsers.toLocaleString()}</h4>
                </div>
                <img src={ImageActiveUsers} alt='active-users' className="my-auto" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={2} lg={4} md={6} sm={12} className="mb-3 mb-md-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="light2 mb-3 gilroy-semibold font-14">Inactive Users</p>
                  <h4 className="mb-0">{stats.inactiveUsers.toLocaleString()}</h4>
                </div>
                <img src={ImageInactiveUsers} alt='inactive-users' className="my-auto" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={2} lg={4} md={6} sm={12} className="mb-3 mb-md-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="light2 mb-3 gilroy-semibold font-14">Blocked Users</p>
                  <h4 className="mb-0">{stats.blockedUsers.toLocaleString()}</h4>
                </div>
                <img src={ImageBlockedUsers} alt='blocked-users' className="my-auto" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={2} lg={4} md={6} sm={12} className="mb-3 mb-md-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="light2 mb-3 gilroy-semibold font-14">Deleted Users</p>
                  <h4 className="mb-0">{stats.deletedUsers.toLocaleString()}</h4>
                </div>
                <img src={ImageDeletedUsers} alt='deleted-users' className="my-auto" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={2} lg={4} md={6} sm={12} className="mb-3 mb-md-2 mb-xl-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <p className="light2 mb-3 gilroy-semibold font-14">Total Revenue</p>
                  <h4 className="mb-0">${stats.totalRevenue.toLocaleString()}</h4>
                </div>
                <img src={ImageTotalRevenue} alt='total-revenue' className="my-auto" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <DateRangeSelector onDateRangeChange={handleDateRangeChange} selectedOption={selectedDateOption} />

      <Row className="mb-4">
        <Col lg={6} className="mb-4 mb-lg-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h4 className="mb-1 text-light">User Growth Trend</h4>
              <p className="mb-4" style={{ color: '#7D8FB3' }}>New registrations over time</p>
              <div style={{ width: '100%', height: 300 }}>
                {userGrowthData.length > 0 ? (
                  <ResponsiveContainer className="custom">
                    <LineChart data={userGrowthData} >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month"
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        interval={0}
                      />
                      <YAxis domain={[0, 200]} ticks={[0, 50, 100, 150, 200]} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="users"
                        stroke="#08A45D"
                        activeDot={{ r: 8 }}
                        
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted gilroy-semibold font-16">No data available</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h4 className="mb-1 text-light">Revenue Breakdown</h4>
              <p className="mb-4" style={{ color: '#7D8FB3' }}>Monthly revenue ($)</p>
              <div style={{ width: '100%', height: 300 }}>
                {revenueData.length > 0 ? (
                  <ResponsiveContainer className="custom">
                    <BarChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month"
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        interval={0}
                      />
                      <YAxis domain={[0, 4000]} ticks={[0, 1000, 2000, 3000, 4000]} />
                      <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']} />
                      <Bar dataKey="revenue" fill="#08A45D" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted gilroy-semibold font-16">No data available</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={4} className="mb-4 mb-md-0 mt-2 mt-lg-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h4 className="mb-1 text-light">License Distribution</h4>
              <p className="mb-4" style={{ color: '#7D8FB3' }}>Number of accounts by license range</p>
              <div style={{ width: '100%', height: 300 }}>
                {licenseData.length > 0 ? (
                  <ResponsiveContainer className="custom small">
                    <BarChart
                      layout="vertical"
                      data={licenseData}
                      margin={{ top: -10, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 200]} ticks={[0, 50, 100, 150, 200]} />
                      <YAxis 
                        dataKey="licenseRange" 
                        type="category"
                        tick={{ fontSize: 10 }}
                        interval={0}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill="#08A45D" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted gilroy-semibold font-16">No data available</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mb-4 mb-md-0 mt-2 mt-lg-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h4 className="mb-1 text-light">Inspection Types</h4>
              <p className="mb-4" style={{ color: '#7D8FB3' }}>Distribution of inspection types</p>
              <div style={{ width: '100%', height: 300 }}>
                {inspectionData.length > 0 ? (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={inspectionData}
                        dataKey="count"
                        nameKey="type"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        // innerRadius={50}
                        label={({ percentage }) => `${percentage}%`}
                        // labelLine={false}
                      >
                        {inspectionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value: number, name: string) => [
                          `${value} (${inspectionData.find(item => item.type === name)?.percentage}%)`, 
                          name
                        ]} 
                      />
                      <Legend 
                        wrapperStyle={{
                          fontSize: '12px',
                          paddingTop: '10px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted gilroy-semibold font-16">No data available</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mt-2 mt-lg-0">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h4 className="mb-1 text-light">Subscription Insights</h4>
              <p className="mb-4" style={{ color: '#7D8FB3' }}>New vs. renewal subscriptions</p>
              <div style={{ width: '100%', height: 300 }}>
                {subscriptionData.length > 0 && subscriptionData.some(item => item.value > 0) ? (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={subscriptionData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ value }) => `${value}%`}
                        // label={({ name, value }) => `${name}: ${value}%`}
                      >
                        {subscriptionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip  />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted gilroy-semibold font-16">No data available</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;